import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Keeps the indicator under the current tab: on every change of tab or items, and whenever the
 * list resizes. Returns the refs to attach to the list, the indicator and each tab button.
 */
export function useSlidingIndicator(current: string, items: unknown[]) {
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const moveIndicator = useCallback(() => {
    const active = buttonRefs.current[current];
    const indicator = indicatorRef.current;
    if (!active || !indicator) return;
    indicator.style.width = `${active.offsetWidth}px`;
    indicator.style.transform = `translateX(${active.offsetLeft}px)`;
  }, [current]);

  useLayoutEffect(() => {
    moveIndicator();
  }, [moveIndicator, items]);

  useResizeObserver(listRef, moveIndicator);

  return { indicatorRef, listRef, buttonRefs };
}

function useResizeObserver(ref: RefObject<HTMLElement | null>, onResize: () => void): void {
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => onResize());
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, onResize]);
}
